"use client";

import { c as _c } from "react/compiler-runtime";
import InfoIcon from "@/assets/icons/info.svg";
import { addUtcMonths, endOfUtcMonth, formatCalendarDate, formatUtcDate, startOfUtcDay, startOfUtcMonth, subUtcMonths } from "@/lib/date-utils";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Tooltip } from "@/shared/ui";
import { cn } from "@/lib/utils";
import { useStreamStatsProgressQuery, useStreamStatsSummaryQuery, type DailyStats } from "@/features/stream-stats/api/stream-stats-queries";
import type { Profile } from "@/features/auth/api/types";
import { Card } from "../card/card";
import { formatHeaderValue, formatSummaryTotal, SeriesKind } from "../../day-series";
import { GameStatsCard } from "../game-stats-card/game-stats-card";
import { MiniBarChart } from "../mini-bar-chart/mini-bar-chart";
import { GAME_STATS_TYPES, STATS_TYPES } from "../../mock-data";
import { clampMonth, isAtMaxMonth } from "../../month-bounds";
import { DateRangePickerSelect } from "../month-picker/month-picker";
import { OverviewProgressChart } from "../overview-progress-chart/overview-progress-chart";
import { SectionContainer } from "../section-container/section-container";
import { SectionHeader } from "../section-header/section-header";
import { getInitialOverviewMonth, getInitialStatsRange, type DateRange } from "../../stream-stats-range";
import { TargetCardsRow } from "../target-cards/target-cards";
import targetCardStyles from "../target-cards/target-cards.module.scss";
import { STREAM_STATS_CONTENT_MESSAGES } from "./messages";
import styles from "./stream-stats-content.module.scss";
function getDailySeriesValue(stat: DailyStats, kind: SeriesKind): number {
  switch (kind) {
    case SeriesKind.Reach:
      return stat.reach;
    case SeriesKind.SharePercent:
      return stat.shareOfReach ?? 0;
    case SeriesKind.AirtimeMinutes:
      return stat.airtimeMinutes;
    case SeriesKind.SharePercentAirtime:
      return stat.shareOfAirtime;
  }
}
export function StreamStatsContent(t0) {
  const $ = _c(77);
  const {
    profile,
    onBack
  } = t0;
  const [month, setMonth] = useState(_temp);
  const [statsRange, setStatsRange] = useState(_temp2);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);
  const statsGridRef = useRef(null);
  let t1;
  if ($[0] !== month) {
    t1 = isAtMaxMonth(month);
    $[0] = month;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isCurrentMonth = t1;
  let t2;
  if ($[2] !== month) {
    t2 = formatUtcDate(startOfUtcMonth(month), "yyyy-MM-dd");
    $[2] = month;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const startDate = t2;
  let t3;
  if ($[4] !== isCurrentMonth || $[5] !== month) {
    t3 = formatUtcDate(isCurrentMonth ? startOfUtcDay(new Date()) : endOfUtcMonth(month), "yyyy-MM-dd");
    $[4] = isCurrentMonth;
    $[5] = month;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const endDate = t3;
  let t4;
  if ($[7] !== statsRange.from) {
    t4 = formatUtcDate(statsRange.from, "yyyy-MM-dd");
    $[7] = statsRange.from;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const statsStartDate = t4;
  let t5;
  if ($[9] !== statsRange.to) {
    t5 = formatUtcDate(statsRange.to, "yyyy-MM-dd");
    $[9] = statsRange.to;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const statsEndDate = t5;
  const statsRangeKey = `${statsStartDate}-${statsEndDate}`;
  const t6 = profile?.id;
  let t7;
  if ($[11] !== endDate || $[12] !== startDate || $[13] !== t6) {
    t7 = {
      profileId: t6,
      startDate,
      endDate
    };
    $[11] = endDate;
    $[12] = startDate;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    data: progressQueryData,
    isLoading: isProgressLoading
  } = useStreamStatsProgressQuery(t7);
  const t8 = profile?.id;
  let t9;
  if ($[15] !== endDate || $[16] !== startDate || $[17] !== t8) {
    t9 = {
      profileId: t8,
      startDate,
      endDate
    };
    $[15] = endDate;
    $[16] = startDate;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const {
    data: progressSummaryData,
    isLoading: isProgressSummaryLoading
  } = useStreamStatsSummaryQuery(t9);
  const t10 = profile?.id;
  let t11;
  if ($[19] !== statsEndDate || $[20] !== statsStartDate || $[21] !== t10) {
    t11 = {
      profileId: t10,
      startDate: statsStartDate,
      endDate: statsEndDate
    };
    $[19] = statsEndDate;
    $[20] = statsStartDate;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const {
    data: statsSummaryData
  } = useStreamStatsSummaryQuery(t11);
  let t12;
  if ($[23] !== statsSummaryData?.dailyStats) {
    t12 = statsSummaryData?.dailyStats ?? [];
    $[23] = statsSummaryData?.dailyStats;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const statsDailyStats = t12;
  const maxDataPoints = statsDailyStats.length;
  const hoveredDayIndex = hoveredDay && hoveredDay.rangeKey === statsRangeKey ? Math.min(Math.max(0, hoveredDay.dayIndex), Math.max(0, maxDataPoints - 1)) : null;
  const defaultDayIndex = Math.max(0, maxDataPoints - 1);
  const isChartInspect = hoveredDayIndex !== null;
  const focusedDayIndex = hoveredDayIndex !== null ? hoveredDayIndex : defaultDayIndex;
  const clampedDayIndex = Math.max(0, Math.min(Math.max(0, maxDataPoints - 1), focusedDayIndex));
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = function handleStatsGridPointerLeave(e) {
      if (e.pointerType === "touch") {
        return;
      }
      const next = e.relatedTarget as Node | null;
      if (statsGridRef.current && next && statsGridRef.current.contains(next)) {
        return;
      }
      setHoveredDay(null);
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleStatsGridPointerLeave = t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = event => {
      if (event.pointerType !== "touch") {
        return;
      }
      setHoveredDay(null);
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const dismissOnOutsideTap = t14;
  let t15;
  let t16;
  if ($[27] !== hoveredDayIndex) {
    t15 = () => {
      if (hoveredDayIndex === null) {
        return;
      }
      document.addEventListener("pointerdown", dismissOnOutsideTap);
      return () => document.removeEventListener("pointerdown", dismissOnOutsideTap);
    };
    t16 = [hoveredDayIndex];
    $[27] = hoveredDayIndex;
    $[28] = t15;
    $[29] = t16;
  } else {
    t15 = $[28];
    t16 = $[29];
  }
  useEffect(t15, t16);
  let t17;
  if ($[30] !== month || $[31] !== onBack || $[32] !== profile) {
    t17 = onBack && profile?.streamingUsername ? `${profile.streamingUsername}'s ${formatUtcDate(month, "MMM")} Overview` : `${formatUtcDate(month, "MMMM")} Overview`;
    $[30] = month;
    $[31] = onBack;
    $[32] = profile;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setMonth(_temp3);
    t19 = () => setMonth(_temp4);
    $[34] = t18;
    $[35] = t19;
  } else {
    t18 = $[34];
    t19 = $[35];
  }
  let t20;
  if ($[36] !== month || $[37] !== onBack || $[38] !== t17) {
    t20 = <SectionHeader title={t17} onBack={onBack} backLabel={STREAM_STATS_CONTENT_MESSAGES.backToStreamers} month={month} onPrev={t18} onNext={t19} prevLabel={STREAM_STATS_CONTENT_MESSAGES.previousMonth} nextLabel={STREAM_STATS_CONTENT_MESSAGES.nextMonth} />;
    $[36] = month;
    $[37] = onBack;
    $[38] = t17;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = profile?.id;
  let t22;
  if ($[40] !== endDate || $[41] !== startDate || $[42] !== t21) {
    t22 = <TargetCardsRow className={targetCardStyles.rowSidebar} profileId={t21} startDate={startDate} endDate={endDate} />;
    $[40] = endDate;
    $[41] = startDate;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = isProgressLoading || isProgressSummaryLoading;
  let t24;
  if ($[44] !== progressQueryData || $[45] !== progressSummaryData || $[46] !== startDate || $[47] !== t23) {
    t24 = <div className={styles.progressChartCell}><Card title={STREAM_STATS_CONTENT_MESSAGES.progressGraphTitle} headerVariant="overview" className={styles.progressChartCard}><OverviewProgressChart progressData={progressQueryData} summaryData={progressSummaryData} isLoading={t23} startDate={startDate} /></Card></div>;
    $[44] = progressQueryData;
    $[45] = progressSummaryData;
    $[46] = startDate;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t22 || $[50] !== t24) {
    t25 = <div className={styles.progressGrid}>{t22}{t24}</div>;
    $[49] = t22;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t20 || $[53] !== t25) {
    t26 = <SectionContainer header={t20} className={styles.sectionFullWidth}>{t25}</SectionContainer>;
    $[52] = t20;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== statsRange) {
    t27 = <SectionHeader title={STREAM_STATS_CONTENT_MESSAGES.streamStatsTitle} rightContent={<DateRangePickerSelect value={statsRange} onChange={setStatsRange} className={styles.statsRangePicker} />} />;
    $[55] = statsRange;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== clampedDayIndex || $[58] !== hoveredCardId || $[59] !== isChartInspect || $[60] !== statsDailyStats || $[61] !== statsRangeKey || $[62] !== statsSummaryData) {
    t28 = STATS_TYPES.map(row => {
      const Icon = row.Icon;
      const isCardHovered = hoveredCardId === row.id;
      const isCardBarInspect = isChartInspect;
      const isCardHoverMode = isCardHovered && !isChartInspect;
      const series = statsDailyStats.map(stat => getDailySeriesValue(stat, row.seriesKind));
      const rowDayIndex = Math.max(0, Math.min(Math.max(0, series.length - 1), clampedDayIndex));
      const rawValue = series[rowDayIndex] ?? 0;
      const inspectedDate = statsDailyStats[rowDayIndex]?.date;
      const inspectedDateLabel = inspectedDate ? formatCalendarDate(inspectedDate, "MMM d") : null;
      const headerDayLabel = isCardBarInspect && inspectedDateLabel ? `${inspectedDateLabel}:` : "";
      const hasData = statsSummaryData?.summary != null && statsSummaryData?.sov != null;
      const monthTotalLabel = hasData ? formatSummaryTotal(row.seriesKind, statsSummaryData.summary, statsSummaryData.sov) : "\u2014 \u2014";
      const showMonthTotal = !isChartInspect && !isCardHoverMode;
      const cardStyle = {
        "--card-accent": row.accentColor
      } as CSSProperties;
      return <Card key={row.id} className={styles.streamStatCard} style={cardStyle} title={row.title} accentColor={row.accentColor} hoverBorderColor={row.accentColor} onHoverChange={hovered => setHoveredCardId(hovered ? row.id : null)} titleTone={isChartInspect || isCardHoverMode ? "accent" : "neutral"} titleIcon={<Icon />} titleRight={isCardBarInspect ? <><span className={styles.headerDayLabel}>{headerDayLabel}</span><span className={styles.headerDayValue}>{formatHeaderValue(row.seriesKind, rawValue)}</span></> : <Tooltip content={row.tooltip} side="top"><button type="button" className={styles.infoButton} aria-label={`About ${row.title}`} onPointerEnter={() => setHoveredDay(null)}><InfoIcon className={styles.barGraphInfoIcon} /></button></Tooltip>}><div className={styles.chartBody} onPointerLeave={e_0 => {
          if (e_0.pointerType === "touch") {
            return;
          }
          const next_0 = e_0.relatedTarget as Node | null;
          if (next_0 && e_0.currentTarget.contains(next_0)) {
            return;
          }
          setHoveredDay(current => current?.cardId === row.id && current.rangeKey === statsRangeKey ? null : current);
        }}><MiniBarChart values={series} coloredBars={isChartInspect || isCardHoverMode} showSelection={isChartInspect} activeDayIndex={rowDayIndex} onDayPointerEnter={idx => setHoveredDay({
            rangeKey: statsRangeKey,
            cardId: row.id,
            dayIndex: idx
          })} accentColor={row.accentColor} /><div className={cn(styles.monthTotalWrap, showMonthTotal ? styles.monthTotalWrapVisible : styles.monthTotalWrapHidden)} aria-hidden={!showMonthTotal}><p className={styles.monthTotal} title={monthTotalLabel}>{monthTotalLabel}</p></div></div></Card>;
    });
    $[57] = clampedDayIndex;
    $[58] = hoveredCardId;
    $[59] = isChartInspect;
    $[60] = statsDailyStats;
    $[61] = statsRangeKey;
    $[62] = statsSummaryData;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== profile?.id || $[65] !== statsEndDate || $[66] !== statsStartDate) {
    t29 = GAME_STATS_TYPES.map(row_0 => <GameStatsCard key={row_0.id} title={row_0.title} tooltip={row_0.tooltip} sort={row_0.sort} profileId={profile?.id} startDate={statsStartDate} endDate={statsEndDate} />);
    $[64] = profile?.id;
    $[65] = statsEndDate;
    $[66] = statsStartDate;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t28 || $[69] !== t29) {
    t30 = <div ref={statsGridRef} className={styles.statsGrid} onPointerLeave={handleStatsGridPointerLeave}>{t28}{t29}</div>;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t27 || $[72] !== t30) {
    t31 = <SectionContainer className={styles.sectionFullWidth} header={t27}>{t30}</SectionContainer>;
    $[71] = t27;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t26 || $[75] !== t31) {
    t32 = <div className={styles.page}>{t26}{t31}</div>;
    $[74] = t26;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  return t32;
}
function _temp4(m_0) {
  return clampMonth(addUtcMonths(m_0, 1));
}
function _temp3(m) {
  return clampMonth(subUtcMonths(m, 1));
}
function _temp2() {
  return getInitialStatsRange();
}
function _temp() {
  return getInitialOverviewMonth();
}